import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiUser, FiPhone, FiBriefcase } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../shared/LoadingScreen';
import './Auth.css';

// Country phone number configurations
const COUNTRY_CONFIGS = {
  ZA: { code: '+27', name: 'South Africa', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ },
  US: { code: '+1', name: 'United States', format: '(XXX) XXX-XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  GB: { code: '+44', name: 'United Kingdom', format: 'XXXX XXX XXXX', length: 10, pattern: /^(\d{4})(\d{3})(\d{4})$/ },
  AU: { code: '+61', name: 'Australia', format: 'XXX XXX XXX', length: 9, pattern: /^(\d{3})(\d{3})(\d{3})$/ },
  CA: { code: '+1', name: 'Canada', format: '(XXX) XXX-XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  IN: { code: '+91', name: 'India', format: 'XXXXX XXXXX', length: 10, pattern: /^(\d{5})(\d{5})$/ },
  NZ: { code: '+64', name: 'New Zealand', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ },
  KE: { code: '+254', name: 'Kenya', format: 'XXX XXXXXX', length: 9, pattern: /^(\d{3})(\d{6})$/ },
  NG: { code: '+234', name: 'Nigeria', format: 'XXX XXX XXXX', length: 10, pattern: /^(\d{3})(\d{3})(\d{4})$/ },
  AE: { code: '+971', name: 'UAE', format: 'XX XXX XXXX', length: 9, pattern: /^(\d{2})(\d{3})(\d{4})$/ }
};

const Signup = () => {
  const [step, setStep] = useState(1); // 1: Role selection, 2: Account details, 3: Google profile completion
  const [authMethod, setAuthMethod] = useState(null); // 'email' or 'google'
  const [googleUser, setGoogleUser] = useState(null); // Store Google user data temporarily
  const [formData, setFormData] = useState({
    role: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    phoneNumber: '',
    businessName: '', // For trainers
    certifications: '', // For trainers
    goals: '' // For clients
  });
  const [countryCode, setCountryCode] = useState('ZA'); // Default to South Africa
  const [phoneInput, setPhoneInput] = useState(''); // Raw phone number input
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { signup, signInWithGoogle, createUserProfile, reloadUserProfile, currentUser } = useAuth();
  const navigate = useNavigate();

  // Handle redirect result after Google Sign-In on mobile
  useEffect(() => {
    const handleRedirectResult = async () => {
      // Check if we have a pending signup from redirect
      const pendingRole = localStorage.getItem('pendingSignupRole');
      const pendingStep = localStorage.getItem('pendingSignupStep');

      if (currentUser && pendingRole && pendingStep) {
        console.log('Handling redirect result for signup');

        // Restore signup state
        setFormData(prev => ({
          ...prev,
          role: pendingRole,
          displayName: currentUser.displayName || '',
          email: currentUser.email || ''
        }));

        setGoogleUser(currentUser);
        setStep(parseInt(pendingStep));

        // Clear pending data
        localStorage.removeItem('pendingSignupRole');
        localStorage.removeItem('pendingSignupStep');
      }
    };

    handleRedirectResult();
  }, [currentUser]);

  // Format phone number based on country
  // Expects clean digits only (no spaces or special characters)
  const formatPhoneNumber = (cleanDigits, country) => {
    const config = COUNTRY_CONFIGS[country];

    // Limit to max length for country
    const limitedDigits = cleanDigits.slice(0, config.length);

    // Apply formatting
    if (config.pattern.test(limitedDigits)) {
      return limitedDigits.replace(config.pattern, (match, ...groups) => {
        // Filter out undefined groups and join with appropriate spacing
        return groups.filter(g => g !== undefined).join(' ');
      });
    }

    // For partial input, apply partial formatting
    if (limitedDigits.length > 0) {
      const formatParts = config.format.split(' ');
      let result = '';
      let digitIndex = 0;

      for (const part of formatParts) {
        const partLength = part.replace(/[^X]/g, '').length;
        const partDigits = limitedDigits.slice(digitIndex, digitIndex + partLength);

        if (partDigits.length === 0) break;

        if (result.length > 0) result += ' ';
        result += partDigits;
        digitIndex += partLength;

        if (digitIndex >= limitedDigits.length) break;
      }

      return result;
    }

    return limitedDigits;
  };

  // Handle phone number input change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Extract only digits from input
    const digits = value.replace(/\D/g, '');
    const config = COUNTRY_CONFIGS[countryCode];

    // Don't allow more digits than the country format allows
    if (digits.length <= config.length) {
      // Format the digits (not the value)
      const formatted = formatPhoneNumber(digits, countryCode);
      setPhoneInput(formatted);

      // Update formData with full international format
      if (digits.length === config.length) {
        setFormData(prev => ({
          ...prev,
          phoneNumber: `${config.code}${digits}`
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          phoneNumber: ''
        }));
      }
    }
  };

  // Handle country code change
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountryCode(newCountry);
    setPhoneInput(''); // Clear phone input when country changes
    setFormData(prev => ({
      ...prev,
      phoneNumber: ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleRoleSelect = (role) => {
    setFormData(prev => ({
      ...prev,
      role
    }));
    // Don't automatically go to step 2, wait for auth method selection
  };

  const handleAuthMethodSelect = (method) => {
    setAuthMethod(method);
    if (method === 'email') {
      setStep(2); // Go to email signup form
    } else if (method === 'google') {
      handleGoogleSignIn(); // Trigger Google sign-in
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.displayName) {
      setError('Please fill in all required fields.');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      const profileData = {
        displayName: formData.displayName,
        role: formData.role,
        phoneNumber: formData.phoneNumber
      };

      // Add role-specific data
      if (formData.role === 'trainer') {
        profileData.businessName = formData.businessName;
        profileData.certifications = formData.certifications
          .split(',')
          .map(cert => cert.trim())
          .filter(cert => cert);
        profileData.specializations = [];
      } else {
        profileData.goals = formData.goals
          .split(',')
          .map(goal => goal.trim())
          .filter(goal => goal);
      }

      await signup(formData.email, formData.password, profileData);

      // Show success message and redirect
      navigate('/verify-email', {
        replace: true,
        state: { email: formData.email }
      });
    } catch (error) {
      console.error('Signup error:', error);

      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address format.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please use a stronger password.');
          break;
        case 'auth/operation-not-allowed':
          setError('Email/password accounts are not enabled. Please contact support.');
          break;
        default:
          setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!formData.role) {
      setError('Please select your account type first.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Save role to localStorage before redirect (for mobile flow)
      localStorage.setItem('pendingSignupRole', formData.role);
      localStorage.setItem('pendingSignupStep', '3');

      const result = await signInWithGoogle();

      // If result is null, we're using redirect flow (mobile)
      // The page will reload and useEffect will handle the rest
      if (!result) {
        return;
      }

      // Popup flow (desktop) - result is available immediately
      // Store Google user data temporarily
      setGoogleUser(result.user);

      // Pre-fill form with Google data
      setFormData(prev => ({
        ...prev,
        displayName: result.user.displayName || '',
        email: result.user.email || ''
      }));

      // Move to profile completion step
      setStep(3);

      // Clear pending data since we handled it
      localStorage.removeItem('pendingSignupRole');
      localStorage.removeItem('pendingSignupStep');
    } catch (error) {
      console.error('Google sign-in error:', error);

      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up blocked. Please allow pop-ups for this site.');
      } else {
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete Google profile
  const handleGoogleProfileComplete = async (e) => {
    e.preventDefault();

    setError('');
    setLoading(true);

    try {
      const profileData = {
        displayName: formData.displayName,
        role: formData.role,
        phoneNumber: formData.phoneNumber
      };

      // Add role-specific data
      if (formData.role === 'trainer') {
        profileData.businessName = formData.businessName;
        profileData.certifications = formData.certifications
          .split(',')
          .map(cert => cert.trim())
          .filter(cert => cert);
        profileData.specializations = [];
      } else {
        profileData.goals = formData.goals
          .split(',')
          .map(goal => goal.trim())
          .filter(goal => goal);
      }

      // Create user profile in Firestore
      await createUserProfile(googleUser, profileData);

      // Reload user profile in context to update state
      await reloadUserProfile(googleUser.uid);

      // Navigate to dashboard
      navigate(formData.role === 'trainer' ? '/unique-physique/trainer-dashboard' : '/unique-physique/client-dashboard', {
        replace: true
      });
    } catch (error) {
      console.error('Profile completion error:', error);
      setError('Failed to complete profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className="auth-card">
          {step === 1 ? (
            // Step 1: Role Selection
            <>
              <div className="auth-header">
                <h1 className="auth-title">Join Unique Fitness</h1>
                <p className="auth-subtitle">Choose your account type to get started</p>
              </div>

              {error && (
                <div className="auth-error">
                  <FiAlertCircle />
                  <span>{error}</span>
                </div>
              )}

              <div className="role-selection">
                <button
                  type="button"
                  className={`role-card ${formData.role === 'trainer' ? 'selected' : ''}`}
                  onClick={() => handleRoleSelect('trainer')}
                >
                  <div className="role-icon">
                    <FiBriefcase size={40} />
                  </div>
                  <h3>I'm a Coach/Trainer</h3>
                  <p>Manage clients, create programs, and grow your business</p>
                  <ul className="role-features">
                    <li>Client management dashboard</li>
                    <li>Program creation tools</li>
                    <li>Progress tracking</li>
                    <li>Business analytics</li>
                  </ul>
                </button>

                <button
                  type="button"
                  className={`role-card ${formData.role === 'client' ? 'selected' : ''}`}
                  onClick={() => handleRoleSelect('client')}
                >
                  <div className="role-icon">
                    <FiUser size={40} />
                  </div>
                  <h3>I'm a Client</h3>
                  <p>Track your nutrition, log meals, and reach your goals</p>
                  <ul className="role-features">
                    <li>Personalized nutrition plans</li>
                    <li>Meal logging</li>
                    <li>Progress visualization</li>
                    <li>Direct coach communication</li>
                  </ul>
                </button>
              </div>

              {formData.role && (
                <>
                  <div className="auth-divider">
                    <span>Choose sign-up method</span>
                  </div>

                  <div className="auth-method-buttons">
                    <button
                      type="button"
                      onClick={() => handleAuthMethodSelect('google')}
                      className="btn-google"
                      disabled={loading}
                    >
                      <FcGoogle size={20} />
                      <span>Continue with Google</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleAuthMethodSelect('email')}
                      className="btn-email"
                      disabled={loading}
                    >
                      <FiMail size={20} />
                      <span>Sign up with Email</span>
                    </button>
                  </div>
                </>
              )}

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login" className="auth-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          ) : step === 2 ? (
            // Step 2: Account Details
            <>
              <div className="auth-header">
                <h1 className="auth-title">Create Your Account</h1>
                <p className="auth-subtitle">
                  {formData.role === 'trainer' ? 'Coach Account' : 'Client Account'}
                </p>
                <button
                  type="button"
                  className="back-button"
                  onClick={() => setStep(1)}
                >
                  ← Change account type
                </button>
              </div>

              {error && (
                <div className="auth-error">
                  <FiAlertCircle />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="displayName">Full Name *</label>
                  <div className="input-wrapper">
                    <FiUser className="input-icon" />
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number (optional)</label>
                  <div className="phone-input-container">
                    <select
                      className="country-select"
                      value={countryCode}
                      onChange={handleCountryChange}
                      aria-label="Country code"
                    >
                      {Object.entries(COUNTRY_CONFIGS).map(([code, config]) => (
                        <option key={code} value={code}>
                          {config.code} {config.name}
                        </option>
                      ))}
                    </select>
                    <div className="input-wrapper phone-input-wrapper">
                      <FiPhone className="input-icon" />
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={phoneInput}
                        onChange={handlePhoneChange}
                        placeholder={COUNTRY_CONFIGS[countryCode].format}
                        aria-label="Phone number"
                      />
                    </div>
                  </div>
                  <small className="input-hint">
                    Format: {COUNTRY_CONFIGS[countryCode].code} {COUNTRY_CONFIGS[countryCode].format}
                  </small>
                </div>

                {formData.role === 'trainer' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="businessName">Business Name (optional)</label>
                      <div className="input-wrapper">
                        <FiBriefcase className="input-icon" />
                        <input
                          type="text"
                          id="businessName"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          placeholder="Your Coaching Business"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="certifications">Certifications (comma-separated, optional)</label>
                      <input
                        type="text"
                        id="certifications"
                        name="certifications"
                        value={formData.certifications}
                        onChange={handleInputChange}
                        placeholder="NASM CPT, Precision Nutrition Level 1"
                        className="text-input"
                      />
                    </div>
                  </>
                )}

                {formData.role === 'client' && (
                  <div className="form-group">
                    <label htmlFor="goals">Your Goals (comma-separated, optional)</label>
                    <input
                      type="text"
                      id="goals"
                      name="goals"
                      value={formData.goals}
                      onChange={handleInputChange}
                      placeholder="Lose weight, Build muscle, Get stronger"
                      className="text-input"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Min. 6 characters"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Re-enter password"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login" className="auth-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          ) : (
            // Step 3: Google Profile Completion
            <>
              <div className="auth-header">
                <h1 className="auth-title">Complete Your Profile</h1>
                <p className="auth-subtitle">
                  {formData.role === 'trainer' ? 'Coach Account' : 'Client Account'}
                </p>
                <p className="email-display">{formData.email}</p>
                <button
                  type="button"
                  className="back-button"
                  onClick={() => {
                    setStep(1);
                    setGoogleUser(null);
                    setAuthMethod(null);
                  }}
                >
                  ← Start over
                </button>
              </div>

              {error && (
                <div className="auth-error">
                  <FiAlertCircle />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleGoogleProfileComplete} className="auth-form">
                <div className="form-group">
                  <label htmlFor="displayName">Full Name *</label>
                  <div className="input-wrapper">
                    <FiUser className="input-icon" />
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number (optional)</label>
                  <div className="phone-input-container">
                    <select
                      className="country-select"
                      value={countryCode}
                      onChange={handleCountryChange}
                      aria-label="Country code"
                    >
                      {Object.entries(COUNTRY_CONFIGS).map(([code, config]) => (
                        <option key={code} value={code}>
                          {config.code} {config.name}
                        </option>
                      ))}
                    </select>
                    <div className="input-wrapper phone-input-wrapper">
                      <FiPhone className="input-icon" />
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={phoneInput}
                        onChange={handlePhoneChange}
                        placeholder={COUNTRY_CONFIGS[countryCode].format}
                        aria-label="Phone number"
                      />
                    </div>
                  </div>
                  <small className="input-hint">
                    Format: {COUNTRY_CONFIGS[countryCode].code} {COUNTRY_CONFIGS[countryCode].format}
                  </small>
                </div>

                {formData.role === 'trainer' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="businessName">Business Name (optional)</label>
                      <div className="input-wrapper">
                        <FiBriefcase className="input-icon" />
                        <input
                          type="text"
                          id="businessName"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          placeholder="Your Coaching Business"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="certifications">Certifications (comma-separated, optional)</label>
                      <input
                        type="text"
                        id="certifications"
                        name="certifications"
                        value={formData.certifications}
                        onChange={handleInputChange}
                        placeholder="NASM CPT, Precision Nutrition Level 1"
                        className="text-input"
                      />
                    </div>
                  </>
                )}

                {formData.role === 'client' && (
                  <div className="form-group">
                    <label htmlFor="goals">Your Goals (comma-separated, optional)</label>
                    <input
                      type="text"
                      id="goals"
                      name="goals"
                      value={formData.goals}
                      onChange={handleInputChange}
                      placeholder="Lose weight, Build muscle, Get stronger"
                      className="text-input"
                    />
                  </div>
                )}

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Completing profile...' : 'Complete Profile'}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login" className="auth-link">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;
